/* Styles for the shared <FindingsBlock> renderer (a ```netwatch-findings
 * block inside an assistant answer). Hosts load this file: the dashboard
 * imports `@netwatchai/chat-core/findings.css`; the widget inlines it into
 * its shadow-root <style>.
 *
 * Colours read the host's `--nw-*` chat-content tokens (the widget sets them
 * per theme in its shadow DOM, the SPA maps them onto its theme tokens) and
 * fall back to the dark chat-content values. The few status colours the
 * tokens don't carry are defined on `.nw-fb` with a `.nw-theme-light`
 * override, the same convention as outlook.css / incidents.css. */

.nw-fb {
  --fb-green: #46b27e;
  --fb-amber: #d99a3a;
  --fb-red: #d9605c;
  --fb-warn-bg: rgba(217, 96, 92, 0.10);
  --fb-warn-line: rgba(217, 96, 92, 0.35);
  --fb-ok-bg: rgba(70, 178, 126, 0.12);
  --fb-ok-line: rgba(70, 178, 126, 0.35);
  --fb-amber-bg: rgba(217, 154, 58, 0.14);
  --fb-amber-line: rgba(217, 154, 58, 0.45);
  --fb-thr: rgba(217, 96, 92, 0.45);

  margin: 10px 0;
  border: 1px solid var(--nw-border, rgba(255, 255, 255, 0.12));
  border-radius: 12px;
  background: var(--nw-bg, #141b25);
  color: var(--nw-text, #e8edf2);
  font-size: 13px;
  line-height: 1.45;
  overflow: hidden;
  /* The strip reflows on the BLOCK's width, not the viewport's: the widget
     panel is a few hundred px wide on a full-width host page. */
  container-type: inline-size;
}
.nw-theme-light .nw-fb {
  --fb-green: #1f8f5a;
  --fb-amber: #b7791f;
  --fb-red: #c8443f;
  --fb-warn-bg: rgba(200, 68, 63, 0.07);
  --fb-warn-line: rgba(200, 68, 63, 0.30);
  --fb-ok-bg: rgba(31, 143, 90, 0.10);
  --fb-ok-line: rgba(31, 143, 90, 0.35);
  --fb-amber-bg: rgba(183, 121, 31, 0.10);
  --fb-amber-line: rgba(183, 121, 31, 0.40);
  --fb-thr: rgba(200, 68, 63, 0.40);
}

/* --- verdict line ------------------------------------------------------- */
.nw-fb-verdict {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px 11px;
  border-bottom: 1px solid var(--nw-border, rgba(255, 255, 255, 0.12));
}
.nw-fb-verdict .nw-sev-icon { margin-top: 1px; }
.nw-fb-verdict-text { flex: 1; min-width: 0; }
.nw-fb-verdict-title { font-size: 15px; font-weight: 700; letter-spacing: -0.01em; }
.nw-fb-verdict-detail {
  margin: 3px 0 0;
  color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7));
}
.nw-fb-caption {
  margin: 2px 0 0;
  font-size: 12px;
  color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5));
}
.nw-fb-state {
  margin-left: 8px;
  vertical-align: middle;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0;
}
.nw-fb-state-confirmed { color: var(--fb-green); background: var(--fb-ok-bg); border-color: var(--fb-ok-line); }
.nw-fb-state-probable { color: var(--fb-amber); background: var(--fb-amber-bg); border-color: var(--fb-amber-line); }
.nw-fb-state-inconclusive { color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); border-color: var(--nw-border, rgba(255, 255, 255, 0.12)); }
.nw-fb-lead {
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5));
  font-variant-numeric: tabular-nums;
}
.nw-fb-checked {
  flex: none;
  text-align: right;
  padding-left: 8px;
  border-left: 1px solid var(--nw-border, rgba(255, 255, 255, 0.12));
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.nw-fb-checked-n { font-size: 18px; font-weight: 700; font-variant-numeric: tabular-nums; line-height: 1.1; }
.nw-fb-checked-l { font-size: 11px; color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); }

/* --- sections ----------------------------------------------------------- */
.nw-fb-sec {
  padding: 12px 14px;
  border-top: 1px solid var(--nw-border, rgba(255, 255, 255, 0.12));
}
.nw-fb-sec:first-of-type { border-top: none; }
.nw-fb-h { margin: 0 0 3px; font-size: 13px; font-weight: 700; }
.nw-fb-p { margin: 0 0 8px; color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); }
.nw-fb-p:last-child { margin-bottom: 0; }
.nw-fb-sub { margin-bottom: 8px; }
.nw-fb-look .nw-fb-p { color: var(--nw-text, #e8edf2); }

/* --- bullet-line sections (recurring / processes / problems / next / footer) */
.nw-fb-lines {
  margin: 0; padding-left: 18px;
  display: flex; flex-direction: column; gap: 4px;
}
.nw-fb-lines li { font-size: 12.5px; line-height: 1.45; color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); }
.nw-fb-lines .nw-fb-lines { margin-top: 3px; gap: 2px; }
.nw-fb-lines .nw-fb-lines li { font-size: 11.5px; color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); }
.nw-fb-lines code { font-family: var(--font-mono, ui-monospace, monospace); font-size: 11.5px; }
.nw-fb-lines-more { color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); }
/* The model's layer (remediation, caveats) — demarcated from the
   deterministic sections above it. */
.nw-fb-footer { border-top-style: dashed; }
.nw-fb-footer .nw-fb-h { margin-top: 10px; }
.nw-fb-footer .nw-fb-h:first-child { margin-top: 0; }
.nw-fb-h-warn { color: var(--nw-warning, #e2b93b); }

/* --- pattern strip -------------------------------------------------------- */
.nw-fb-banner {
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid var(--nw-border, rgba(255, 255, 255, 0.12));
  background: var(--nw-bg-subtle, rgba(255, 255, 255, 0.04));
  margin-bottom: 10px;
}
.nw-fb-banner-warn { background: var(--fb-warn-bg); border-color: var(--fb-warn-line); }
.nw-fb-days {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 6px;
}
.nw-fb-day {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 6px 4px 5px;
  border: 1px solid var(--nw-border, rgba(255, 255, 255, 0.12));
  border-radius: 8px;
  min-width: 0;
}
.nw-fb-day-spike { border-color: var(--fb-warn-line); background: var(--fb-warn-bg); }
.nw-fb-day-clean { background: var(--fb-ok-bg); border-color: var(--fb-ok-line); }
.nw-fb-day-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.nw-fb-day-body {
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}
.nw-fb-spark { display: block; max-width: 100%; height: 26px; }
.nw-fb-spark-line { fill: none; stroke: var(--fb-red); stroke-width: 1.5; stroke-linejoin: round; stroke-linecap: round; }
.nw-fb-spark-thr { stroke: var(--fb-thr); stroke-width: 1; stroke-dasharray: 2 2; }
.nw-fb-day-peak { font-size: 11px; font-weight: 600; color: var(--fb-red); font-variant-numeric: tabular-nums; }
.nw-fb-day-none { color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); }
.nw-fb-day-cap {
  font-size: 10.5px;
  color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.nw-fb-day-spike .nw-fb-day-cap { color: var(--fb-red); font-weight: 600; }

/* A narrow widget panel: two rows of cards rather than seven slivers. */
@container (max-width: 480px) {
  .nw-fb-days { grid-template-columns: repeat(4, minmax(0, 1fr)); }
}

/* --- movers --------------------------------------------------------------- */
.nw-fb-mover {
  margin-top: 8px;
  border: 1px solid var(--nw-border, rgba(255, 255, 255, 0.12));
  border-radius: 8px;
  overflow: hidden;
}
.nw-fb-mover-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  background: var(--nw-bg-subtle, rgba(255, 255, 255, 0.04));
}
.nw-fb-mover-label { font-weight: 600; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.nw-fb-mover-val { font-variant-numeric: tabular-nums; white-space: nowrap; }
.nw-fb-mover-base { color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); }
.nw-fb-mover-corr {
  font-variant-numeric: tabular-nums;
  color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7));
  font-size: 12px;
  white-space: nowrap;
}
.nw-fb-mover-chart { padding: 8px 8px 4px; }
.nw-fb-mover-in_band .nw-fb-mover-row { background: transparent; }

/* --- pills ---------------------------------------------------------------- */
.nw-fb-pill {
  flex: none;
  font-size: 11px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 999px;
  border: 1px solid transparent;
  white-space: nowrap;
  text-transform: lowercase;
}
.nw-fb-pill-normal { color: var(--fb-green); background: var(--fb-ok-bg); border-color: var(--fb-ok-line); }
.nw-fb-pill-warn { color: var(--fb-amber); background: var(--fb-amber-bg); border-color: var(--fb-amber-line); }
.nw-fb-pill-strong { color: var(--fb-red); background: var(--fb-warn-bg); border-color: var(--fb-warn-line); }
.nw-fb-pill-inband { color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); border-color: var(--nw-border, rgba(255, 255, 255, 0.12)); }
.nw-fb-check-tag {
  margin-left: 6px;
  font-size: 11px;
  color: var(--fb-amber);
}

/* --- slate rows ------------------------------------------------------- */
.nw-fb-slate-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 7px 10px;
  border-top: 1px solid var(--nw-border, rgba(255, 255, 255, 0.12));
}
.nw-fb-slate-row:first-child { border-top: 0; }
.nw-fb-slate-hyp { flex: 1; min-width: 0; }
.nw-fb-slate-detail {
  flex-basis: 100%;
  font-size: 12px;
  color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7));
}
.nw-fb-slate-row { flex-wrap: wrap; }
.nw-fb-pill-muted { color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); border-color: var(--nw-border, rgba(255, 255, 255, 0.12)); }

/* --- normal groups -------------------------------------------------------- */
.nw-fb-groups { margin-top: 4px; }
.nw-fb-group { border-top: 1px solid var(--nw-border, rgba(255, 255, 255, 0.12)); }
.nw-fb-group:first-child { border-top: none; }
.nw-fb-group-row {
  all: unset;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 4px;
  cursor: pointer;
  color: inherit;
  font: inherit;
}
.nw-fb-group-row:hover { background: var(--nw-bg-subtle, rgba(255, 255, 255, 0.04)); }
.nw-fb-group-row:focus-visible { outline: 2px solid var(--nw-accent, #5b93e8); outline-offset: -2px; border-radius: 6px; }
.nw-fb-group-caret {
  width: 12px;
  color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5));
  font-size: 11px;
  transition: transform 120ms ease;
}
.nw-fb-group.open .nw-fb-group-caret { transform: rotate(90deg); }
.nw-fb-group-label { flex: 1; font-weight: 600; }
.nw-fb-group-count { color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); font-size: 12px; white-space: nowrap; }
.nw-fb-checks {
  width: 100%;
  border-collapse: collapse;
  margin: 0 0 8px;
  font-size: 12.5px;
}
.nw-fb-checks td {
  padding: 4px 6px 4px 24px;
  border-top: 1px dashed var(--nw-border, rgba(255, 255, 255, 0.12));
  vertical-align: middle;
}
.nw-fb-checks td + td { padding-left: 6px; }
.nw-fb-check-label { color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); }
.nw-fb-check-val {
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
/* Column heads appear only when the rows carry spike-window ranges. */
.nw-fb-checks-head th {
  padding: 6px 6px 2px 24px;
  font-size: 11px;
  font-weight: 500;
  color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5));
  text-align: right;
  white-space: nowrap;
}
.nw-fb-checks-head th + th { padding-left: 6px; }
.nw-fb-gaps .nw-fb-group-label { color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); }
.nw-fb-gaps-list {
  padding: 2px 4px 10px 24px;
  color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5));
  font-size: 12px;
}

/* --- fallbacks / loading -------------------------------------------------- */
.nw-fb-note { padding: 10px 14px; color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); }
.nw-fb-rawwrap { padding: 0 14px 10px; }
.nw-fb-rawwrap summary { cursor: pointer; font-size: 12px; color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); }
.nw-fb-raw {
  margin: 6px 0 0;
  max-height: 200px;
  overflow: auto;
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-all;
}
.nw-fb-skel { padding: 14px; }
.nw-fb-skel-bar {
  height: 12px;
  border-radius: 6px;
  background: var(--nw-bg-subtle, rgba(255, 255, 255, 0.06));
  margin-bottom: 8px;
  animation: nw-fb-pulse 1.2s ease-in-out infinite;
}
.nw-fb-skel-bar.short { width: 55%; margin-bottom: 0; }
@keyframes nw-fb-pulse { 0%, 100% { opacity: 0.55; } 50% { opacity: 1; } }

/* Processes measured across the spike windows: name · min–max · windows.
   Same table as the groups, without the group caret indent. */
.nw-fb-procs td, .nw-fb-procs .nw-fb-checks-head th { padding-left: 6px; }
.nw-fb-proc-name {
  font-family: var(--font-mono, ui-monospace, monospace);
  font-size: 12px;
  word-break: break-all;
}
.nw-fb-proc-win {
  text-align: right;
  white-space: nowrap;
  font-size: 11.5px;
  font-variant-numeric: tabular-nums;
  color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5));
}
.nw-fb-note-small { margin: 0 0 8px; font-size: 11.5px; color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); }

/* --- producer tables (docs/findings-everywhere.md) ------------------------ */
/* A titled grid of formatted string cells — outages, hosts below SLA, drives
   filling, lockouts by caller. Same table as the groups/processes, with real
   column heads; wide grids scroll inside the card, never the page. */
.nw-fb-tablewrap { overflow-x: auto; max-width: 100%; }
.nw-fb-table td, .nw-fb-table .nw-fb-checks-head th { padding-left: 6px; }
.nw-fb-table td { white-space: nowrap; font-variant-numeric: tabular-nums; }
.nw-fb-table .nw-fb-cell-left, .nw-fb-table .nw-fb-checks-head th.nw-fb-cell-left { text-align: left; }
.nw-fb-table .nw-fb-cell-right, .nw-fb-table .nw-fb-checks-head th.nw-fb-cell-right { text-align: right; }
.nw-fb-table .nw-fb-cell-lead { color: var(--nw-text, #e8edf2); font-weight: 600; }
.nw-fb-row-warn .nw-fb-cell-lead { color: var(--fb-amber); }
.nw-fb-row-strong .nw-fb-cell-lead { color: var(--fb-red); }
.nw-fb-row-muted td { color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); }
.nw-fb-row-muted .nw-fb-cell-lead { color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); font-weight: 500; }
.nw-fb-row-indent .nw-fb-cell-lead { padding-left: 22px; font-weight: 500; color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); }
.nw-fb-row-indent td { border-top-style: dotted; }

/* The red verdict: the Disaster octagon leads the line and the title takes
   its colour, so "Offline now" reads as red before the words do. */
.nw-fb-critical .nw-fb-verdict-title { color: var(--fb-red); }
